/*
 * Copyright 1999-2024 Jagex Ltd.
 */
import React, { FunctionComponent, lazy, Suspense } from 'react';
import { Switch, Route, BrowserRouter } from 'react-router-dom';
import { Routes } from '@common/routes';
import { LocalizationSwitch } from '@common/routes/components';
import { ProtectedRoute, SwitchWithNotFound } from '@components/appNavigation';
import { MetaHelmet } from '@components/head/meta/MetaHelmet/MetaHelmet';
import { Loader, FlexContainer } from '@components/layouts';
import { LocalizationLoader } from '@i18n/LocalizationLoader';
import CommonErrorPage from '@views/commonErrorPage/CommonErrorPage';
import NotFoundPage from '@views/notFound/NotFoundPage';
import { I18n } from './i18n';

const AccountRoutes = lazy(() => import('@views/account/AccountRoutes'));
const AuthRoutes = lazy(() => import('@views/auth/AuthRoutes'));
const SocialRoutes = lazy(() => import('@views/social/SocialRoutes'));

interface Props {
  i18nInstance: I18n;
}

export const AppRouter: FunctionComponent<Props> = ({ i18nInstance }) => (
  <FlexContainer justifyContent="center" alignItems="center">
    <BrowserRouter>
      <LocalizationLoader i18n={i18nInstance}>
        <Suspense fallback={<Loader />}>
          <MetaHelmet />
          <SwitchWithNotFound>
            <LocalizationSwitch>
              <Switch>
                <Route path={Routes.Auth.route} component={AuthRoutes} />
                <ProtectedRoute path={Routes.Account.route} component={AccountRoutes} />
                <Route path={Routes.Social.route} component={SocialRoutes} />
                <Route exact path={Routes.NotFound.route} component={NotFoundPage} />
                <Route exact path={Routes.CommonError.route} component={CommonErrorPage} />
                <Route component={NotFoundPage} />
              </Switch>
            </LocalizationSwitch>
          </SwitchWithNotFound>
        </Suspense>
      </LocalizationLoader>
    </BrowserRouter>
  </FlexContainer>
);
